import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shotsApi } from '@/api/shots';
import { ShotData } from '../types';
import { toast } from '@/hooks/use-toast';

// Hook to fetch shots for a session
export const useSessionShots = (sessionId: string | undefined) => {
  return useQuery({
    queryKey: ['shots', 'session', sessionId],
    queryFn: () => shotsApi.getSessionShots(sessionId!),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on error - use sample data instead
  });
};

// Hook to fetch shots for a player
export const usePlayerShots = (
  playerId: string | undefined,
  params?: {
    startDate?: string;
    endDate?: string;
    shotType?: string;
    limit?: number;
  }
) => {
  return useQuery({
    queryKey: ['shots', 'player', playerId, params],
    queryFn: () => shotsApi.getPlayerShots(playerId!, params),
    enabled: !!playerId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to get detailed shot metrics
export const useShotMetrics = (shotId: string | undefined) => {
  return useQuery({
    queryKey: ['shots', 'metrics', shotId],
    queryFn: () => shotsApi.getShotMetrics(shotId!),
    enabled: !!shotId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to analyze shot physics
export const useAnalyzeShot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (shotData: Partial<ShotData>) => shotsApi.analyzeShot(shotData),
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['shots'] });
      toast({
        title: 'Shot Analyzed',
        description: `Quality: ${data.analysis.quality}`,
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast({
        title: 'Analysis Failed',
        description: error.response?.data?.message || 'Failed to analyze shot',
        variant: 'destructive',
      });
    },
  });
};

// Hook for bulk shot upload
export const useBulkUploadShots = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, shots }: { sessionId: string; shots: Partial<ShotData>[] }) =>
      shotsApi.bulkUpload(sessionId, shots),
    onSuccess: (data, variables) => {
      // Invalidate session shots
      queryClient.invalidateQueries({ queryKey: ['shots', 'session', variables.sessionId] });
      toast({
        title: 'Shots Uploaded',
        description: `Successfully uploaded ${data.uploaded} shots`,
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.message || 'Failed to upload shots',
        variant: 'destructive',
      });
    },
  });
};

// Hook to compare multiple shots
export const useCompareShots = (shotIds: string[], metrics?: string[]) => {
  return useQuery({
    queryKey: ['shots', 'comparison', shotIds, metrics],
    queryFn: () => shotsApi.compareShots(shotIds, metrics),
    enabled: shotIds.length >= 2,
    staleTime: 10 * 60 * 1000,
  });
};

// Hook to get aggregated statistics
export const useShotAggregates = (params: {
  playerId?: string;
  sessionId?: string;
  startDate?: string;
  endDate?: string;
  groupBy?: 'day' | 'week' | 'month' | 'shotType';
}) => {
  return useQuery({
    queryKey: ['shots', 'aggregate', params],
    queryFn: () => shotsApi.getAggregateStats(params),
    enabled: !!(params.playerId || params.sessionId),
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to get recent shots (for live monitoring)
export const useRecentShots = (limit: number = 10, refreshInterval?: number) => {
  return useQuery({
    queryKey: ['shots', 'recent', limit],
    queryFn: () => shotsApi.getRecentShots(limit),
    refetchInterval: refreshInterval || false,
    staleTime: 30 * 1000, // 30 seconds
    retry: false, // Don't retry on error - use sample data instead
  });
};

// Hook for live shot monitoring with WebSocket integration
export const useLiveShots = (sessionId?: string, enableRealTime: boolean = true) => {
  const query = useQuery({
    queryKey: ['shots', 'live', sessionId],
    queryFn: () => sessionId ? shotsApi.getSessionShots(sessionId) : shotsApi.getRecentShots(50),
    enabled: !!sessionId || !sessionId,
    refetchInterval: enableRealTime ? 5000 : false, // Poll every 5 seconds
    staleTime: 2 * 1000, // 2 seconds
  });

  // TODO: Add WebSocket integration here
  // useEffect(() => {
  //   if (enableRealTime && sessionId) {
  //     const socket = io();
  //     socket.on(`session:${sessionId}:shot`, (newShot: ShotData) => {
  //       queryClient.setQueryData(['shots', 'live', sessionId], (old: ShotData[] = []) => 
  //         [newShot, ...old]
  //       );
  //     });
  //     return () => socket.disconnect();
  //   }
  // }, [sessionId, enableRealTime, queryClient]);

  return query;
};

// Hook for analytics dashboard data
export const useAnalyticsDashboard = (params: {
  playerId?: string;
  sessionId?: string;
  startDate?: string;
  endDate?: string;
  refreshInterval?: number;
}) => {
  return useQuery({
    queryKey: ['analytics', 'dashboard', params],
    queryFn: async () => {
      // Fetch multiple data sets for dashboard
      const [shots, aggregates, trends] = await Promise.all([
        params.playerId 
          ? shotsApi.getPlayerShots(params.playerId, params)
          : params.sessionId 
            ? shotsApi.getSessionShots(params.sessionId)
            : shotsApi.getRecentShots(100),
        shotsApi.getAggregateStats(params),
        shotsApi.getTrendData(params)
      ]);
      
      return { shots, aggregates, trends };
    },
    enabled: !!(params.playerId || params.sessionId),
    refetchInterval: params.refreshInterval || 30000, // 30 seconds
    staleTime: 10 * 1000, // 10 seconds
  });
};

// Hook for shot performance trends
export const useShotTrends = (params: {
  playerId?: string;
  sessionId?: string;
  groupBy?: 'day' | 'week' | 'month';
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['shots', 'trends', params],
    queryFn: () => shotsApi.getTrendData(params),
    enabled: !!(params.playerId || params.sessionId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for field distribution analysis
export const useFieldDistribution = (params: {
  playerId?: string;
  sessionId?: string;
  shotType?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['shots', 'field-distribution', params],
    queryFn: () => shotsApi.getFieldDistribution(params),
    enabled: !!(params.playerId || params.sessionId),
    staleTime: 5 * 60 * 1000,
  });
};

// Export parameters interface
interface ExportParams {
  format: 'csv' | 'excel' | 'pdf';
  data: Record<string, unknown>;
  filename?: string;
}

// Export result interface
interface ExportResult extends Blob {
  format?: string;
}

// Hook to export analytics data
export const useExportAnalytics = () => {
  return useMutation({
    mutationFn: (params: ExportParams) => shotsApi.exportData(params),
    onSuccess: (data: ExportResult) => {
      // Trigger download
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cricket-analytics-${Date.now()}.${data.format || 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Export Complete',
        description: 'Analytics data has been downloaded successfully',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast({
        title: 'Export Failed',
        description: error.response?.data?.message || 'Failed to export data',
        variant: 'destructive',
      });
    },
  });
};